import MaterialIconsReact from "material-icons-react";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./Sidebar.module.css";
import { useLocation } from "react-router";
import { UserContext } from "../context/UserContext";
import { TARS_MODULES } from "../pages/InvoiceManagement/constants/modules";
import { useIsMobile } from "../hooks/useIsMobile";

const FACTORY_WARNING_TOAST_ID = "kws-factory-warning";

function Sidebar({ user }) {
  const [menu, setMenu] = useState("");
  const location = useLocation();
  const { openMenu, setOpenMenu, isKwsFactory, invoiceFactory, setToastList } = useContext(UserContext);
  const navigate = useNavigate();
  const toastTimerRef = useRef(null);
  const isMobile = useIsMobile();

  // Cleanup timer on unmount to prevent state update on unmounted component
  useEffect(() => {
    return () => clearTimeout(toastTimerRef.current);
  }, []);

  useEffect(() => {
    setMenu(location.pathname);
  }, [location]);

  const handleModuleClick = useCallback((module) => {
    if (module.production === true && invoiceFactory?.item_code === "all") {
      // Replace any existing factory warning toast (prevents stacking on rapid clicks)
      setToastList((prev) => [
        ...prev.filter((t) => t.id !== FACTORY_WARNING_TOAST_ID),
        { id: FACTORY_WARNING_TOAST_ID, text: "Select a specific factory to access this module", type: "warning" },
      ]);
      // Auto-dismiss after 5s
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = setTimeout(() => {
        setToastList((prev) => prev.filter((t) => t.id !== FACTORY_WARNING_TOAST_ID));
      }, 5000);
      return;
    }
    navigate(module.link);
    // Close sidebar on mobile after navigation
    if (isMobile) {
      setOpenMenu(false);
      localStorage.setItem('isMenuOpen', false);
    }
  }, [invoiceFactory, navigate, setToastList, setOpenMenu, isMobile]);

  // Show/hide production modules based on KWS factory check.
  // Production modules have no bouncerKey — KWS detection is the sole gate.
  // When isKwsFactory is true, all production modules are injected regardless
  // of Bouncer permissions (this is intentional: KWS implies production access).
  const visibleModules = useMemo(() => {
    let storedModules;
    try {
      storedModules = JSON.parse(sessionStorage.getItem("TARS_MODULES_ACCESS")) || [];
    } catch {
      storedModules = [];
    }
    if (isKwsFactory !== true) {
      return storedModules.filter((m) => !m.production);
    }
    // KWS confirmed — ensure production modules are present
    const storedLinks = new Set(storedModules.map((m) => m.link));
    const missingProduction = TARS_MODULES.filter(
      (m) => m.production === true && !storedLinks.has(m.link)
    );
    return [...missingProduction, ...storedModules].sort(
      (a, b) => TARS_MODULES.findIndex((m) => m.link === a.link) - TARS_MODULES.findIndex((m) => m.link === b.link)
    );
  }, [isKwsFactory]);

  return (
    <aside className={styles.sidebar + ' ' + (openMenu ? styles.open : '')}>
      {/* <div className={styles.logoContainer}></div> */}
      <ul className={styles.navBlock}>
        {visibleModules.map((module) =>
          module ? (
            <li className={menu === module.link ? styles.selected : null} key={module.link} title={module.label} onClick={() => handleModuleClick(module)}>
              <div className={styles.navIcons}>
                {menu.indexOf(module.searchObj) > -1 && (
                  <>
                    <MaterialIconsReact
                      icon={module.icon}
                      size={20}
                      color={"#2B44C1"}
                    />
                    {openMenu && <span>{module.label}</span>}
                  </>
                )}
                {menu.indexOf(module.searchObj) === -1 && (
                  <>
                    <MaterialIconsReact
                      icon={module.icon}
                      size={20}
                      color={"#666666"}
                    />
                    {openMenu && <span>{module.label}</span>}
                  </>
                )}
              </div>
            </li>
          ) : null
        )}
      </ul>
    </aside>
  );
}

export default Sidebar;
